import { useLazoView } from "@/contexts/CurrentLazoViewContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { useLazoUser } from "@/contexts/UserContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { useTenantSelect } from "@/hooks/useTenantSelect";
import { Business, ExpandMore, Logout, Settings } from "@mui/icons-material";
import { Box, Collapse, ListItemIcon, MenuItem, Typography, styled } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import CompanyAvatar from "../common/CompanyAvatar";
import { DropdownMenu } from "../common/DropdownMenu";

const StyledNavLink = styled(NavLink)({
  textDecoration: "none",
  color: "inherit",
  display: "flex",
  width: "100%",
});

export function AccountMenu() {
  const { t } = useTranslation("navigation");
  const { currentCompany, currentTenantMembership } = useTenantMemberships();
  const { lazoUser } = useLazoUser();
  const { isPortal } = useLazoView();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [switchCompanyExpanded, setSwitchCompanyExpanded] = useState(false);

  const { items, handleChange } = useTenantSelect();

  const userName = lazoUser?.user ? `${lazoUser.user.firstName} ${lazoUser.user.lastName}` : "";
  const userEmail = lazoUser?.user?.email || "";
  const logoUrl = currentCompany?.logoUrl;

  return (
    <>
      <DropdownMenu
        isOpen={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <CompanyAvatar companyName={userName} logoUrl={logoUrl} size={1.75} borderWidth={0} backgroundColor={Colors.LAZO_PURPLE} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h6"
                sx={{
                  color: Colors.PURPLE_6750A4,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {userName}
              </Typography>
              {isOpen && <Typography sx={{ color: Colors.DARKER_DARKER_GRAY, fontSize: "0.75rem" }}> {userEmail}</Typography>}
            </Box>
          </Box>
        }
      >
        <MenuItem
          onClick={() => {
            setSwitchCompanyExpanded(!switchCompanyExpanded);
          }}
          sx={{ color: Colors.PURPLE_6750A4, display: "flex", justifyContent: "space-between" }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ListItemIcon sx={{ color: Colors.PURPLE_6750A4, minWidth: 36 }}>
              <Business fontSize="small" />
            </ListItemIcon>
            <Typography sx={{ color: Colors.PURPLE_6750A4 }}>{t("accountMenu.switchCompany")}</Typography>
          </Box>
          <ExpandMore
            sx={{
              color: Colors.PURPLE_6750A4,
              transform: switchCompanyExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </MenuItem>

        <Collapse in={switchCompanyExpanded} timeout="auto" unmountOnExit>
          {items.map((i) => {
            const normalizedWorkspaceSlug = StringUtils.capitalizeWords(i.workspaceSlug.replaceAll("-", " "));

            return (
              <Box key={i.workspaceSlug}>
                <Typography
                  sx={{
                    color: Colors.PURPLE_6750A4,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    padding: "0.5rem 1rem",
                    backgroundColor: Colors.LIGHT_GRAY_F3F3F3,
                  }}
                >
                  {t("accountMenu.workspacePrefix")}
                  {normalizedWorkspaceSlug}
                </Typography>
                {i.tenants.map((t) => {
                  const isCurrentTenant = t.externalId === currentTenantMembership?.tenant?.externalId;
                  return (
                    <MenuItem
                      key={t.externalId}
                      onClick={() => {
                        handleChange(t.externalId);
                        setSwitchCompanyExpanded(false);
                        setIsOpen(false);
                      }}
                      sx={{
                        color: Colors.PURPLE_6750A4,
                        paddingLeft: "1rem",
                        paddingRight: "1rem",
                        fontSize: "0.875rem",
                        fontWeight: isCurrentTenant ? 700 : 400,
                        backgroundColor: isCurrentTenant ? Colors.LIGHT_PURPLE_F6F3FF : "transparent",
                        borderLeft: isCurrentTenant ? `3px solid ${Colors.PURPLE_6750A4}` : "none",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: 1.3,
                        textAlign: "center",
                        justifyContent: "center",
                        "&:hover": {
                          backgroundColor: Colors.LIGHT_GRAY_F3F3F3,
                        },
                      }}
                    >
                      {t.name}
                    </MenuItem>
                  );
                })}
              </Box>
            );
          })}
        </Collapse>

        <MenuItem sx={{ color: Colors.PURPLE_6750A4, padding: 0 }}>
          <StyledNavLink
            to={isPortal ? LazoPaths.PORTAL_SETTINGS : LazoPaths.ONE_SETTINGS}
            onClick={() => setIsOpen(false)}
            style={{ padding: "6px 16px" }}
          >
            <ListItemIcon sx={{ color: Colors.PURPLE_6750A4, minWidth: 36 }}>
              <Settings fontSize="small" />
            </ListItemIcon>
            <Typography sx={{ color: Colors.PURPLE_6750A4 }}>{t("accountMenu.settings")}</Typography>
          </StyledNavLink>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setIsOpen(false);
            navigate(LazoPaths.LOGOUT);
          }}
          sx={{ color: Colors.PURPLE_6750A4 }}
        >
          <ListItemIcon sx={{ color: Colors.PURPLE_6750A4, minWidth: 36 }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          <Typography sx={{ color: Colors.PURPLE_6750A4 }}>{t("accountMenu.signOut")}</Typography>
        </MenuItem>
      </DropdownMenu>
    </>
  );
}
